import { createApi } from '@reduxjs/toolkit/query/react';
import { BackendResponse } from '../../types';
import { baseQueryWithReauth } from './baseQuery';
import { parseQueryParams } from '../../utils';

export type SalesOrderDashboardPeriodType = 'week' | 'month' | 'quarter' | 'financialYear';

export interface SalesOrderDashboardMetrics {
  salesOrderCount: number;
  sumOrderGrossValue: number;
  sumOrderNetValue: number;
  sumOrderGrossValueLocal: number;
  sumOrderNetValueLocal: number;
  sumTaxes: number;
  sumNoOfCapsules: number;
}

export interface SalesOrderDashboardPeriodBucket extends SalesOrderDashboardMetrics {
  label: string;
  year: number;
  startDate?: string;
  endDate?: string;
  monthNumber?: number;
  quarterNumber?: number;
}

export interface SalesOrderDashboardCountryRow extends SalesOrderDashboardMetrics {
  countryId: number;
  countryName: string;
}

export interface SalesOrderDashboardCountryPeriodBucket
  extends Omit<SalesOrderDashboardPeriodBucket, keyof SalesOrderDashboardMetrics> {
  countries: SalesOrderDashboardCountryRow[];
}

export interface SalesOrderDashboardNewCustomerPeriodBucket
  extends SalesOrderDashboardPeriodBucket {
  newCustomerCount: number;
  newProductCount: number;
  newCustomer?: Partial<SalesOrderDashboardMetrics> & { salesOrderCount?: number };
  newProduct?: Partial<SalesOrderDashboardMetrics> & { salesOrderCount?: number };
  newCustomerExistingProduct?: SalesOrderDashboardMetrics;
  existingCustomerNewProduct?: SalesOrderDashboardMetrics;
  newCustomerNewProduct?: SalesOrderDashboardMetrics;
  existingCustomerExistingProduct?: SalesOrderDashboardMetrics;
  combinations?: {
    newCustomerExistingProduct?: SalesOrderDashboardMetrics;
    existingCustomerNewProduct?: SalesOrderDashboardMetrics;
    newCustomerNewProduct?: SalesOrderDashboardMetrics;
    existingCustomerExistingProduct?: SalesOrderDashboardMetrics;
  };
}

export interface SalesOrderDashboardEnvelope<T> {
  type: SalesOrderDashboardPeriodType;
  data: Record<string, T>;
}

export interface SalesOrderStatusMetricNode {
  label: string;
  salesOrderCount: number;
  sumOrderGrossValue: number;
  sumOrderNetValue: number;
  sumOrderGrossValueLocal: number;
  sumOrderNetValueLocal: number;
  sumNoOfCapsules: number;
}

export interface SalesOrderOpenOrderNode extends SalesOrderStatusMetricNode {
  yetToApprovedByPPIC: SalesOrderStatusMetricNode;
  approvedByPPIC: SalesOrderStatusMetricNode;
}

export interface SalesOrderStatusSummaryBucket {
  label: string;
  year: number;
  startDate?: string;
  endDate?: string;
  monthNumber?: number;
  quarterNumber?: number;
  totalSalesOrder: SalesOrderStatusMetricNode;
  openOrder: SalesOrderOpenOrderNode;
  closedOrder: SalesOrderStatusMetricNode;
}

type DashboardQueryArg = { type?: SalesOrderDashboardPeriodType };

const salesOrderDashboardApi = createApi({
  reducerPath: 'salesOrderDashboard',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['SalesOrderDashboard'],
  endpoints: builder => ({
    getSalesOrderTrendOrders: builder.query<
      BackendResponse<SalesOrderDashboardEnvelope<SalesOrderDashboardPeriodBucket>>,
      DashboardQueryArg
    >({
      query: params => ({
        url: `/sales-order-dashboard/trend-orders${parseQueryParams(params)}`,
        method: 'GET',
      }),
      providesTags: ['SalesOrderDashboard'],
    }),
    getSalesOrderSalesByCountry: builder.query<
      BackendResponse<
        SalesOrderDashboardEnvelope<SalesOrderDashboardCountryPeriodBucket>
      >,
      DashboardQueryArg
    >({
      query: params => ({
        url: `/sales-order-dashboard/sales-by-country${parseQueryParams(params)}`,
        method: 'GET',
      }),
      providesTags: ['SalesOrderDashboard'],
    }),
    getSalesOrderNewCustomerProduct: builder.query<
      BackendResponse<
        SalesOrderDashboardEnvelope<SalesOrderDashboardNewCustomerPeriodBucket>
      >,
      DashboardQueryArg
    >({
      query: params => ({
        url: `/sales-order-dashboard/new-customer-product${parseQueryParams(params)}`,
        method: 'GET',
      }),
      providesTags: ['SalesOrderDashboard'],
    }),
    getSalesOrderOrderStatusSummary: builder.query<
      BackendResponse<
        SalesOrderDashboardEnvelope<SalesOrderStatusSummaryBucket>
      >,
      DashboardQueryArg
    >({
      query: params => ({
        url: `/sales-order-dashboard/order-status-summary${parseQueryParams(params)}`,
        method: 'GET',
      }),
      providesTags: ['SalesOrderDashboard'],
    }),
  }),
});

export default salesOrderDashboardApi;

export const {
  useGetSalesOrderTrendOrdersQuery,
  useGetSalesOrderSalesByCountryQuery,
  useGetSalesOrderNewCustomerProductQuery,
  useGetSalesOrderOrderStatusSummaryQuery,
} = salesOrderDashboardApi;
